import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { Project } from "../lib/api";
import { thumbOf, thumbAltOf } from "../lib/api";
import { formatProjectYear } from "../lib/dates";
import "./ProjectCard.scss";

export function ProjectCard({ project, index = 0 }: { project: Project; index?: number }) {
  const thumb = thumbOf(project);
  const alt = thumbAltOf(project);
  const year = formatProjectYear(project.date);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // Load full first page eagerly so mid-grid never stays black on first paint
  const eager = index < 24;

  // Handle cache hits (onLoad may not fire if already complete when attached)
  const imgRef = useCallback((node: HTMLImageElement | null) => {
    if (!node) return;
    if (node.complete && node.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  return (
    <article
      className="project-card"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    >
      <Link to={`/project/${project.slug}`} className="project-card__link">
        <div
          className={`project-card__media${thumb && !loaded && !failed ? " is-loading" : ""}`}
        >
          {thumb && !failed ? (
            <img
              ref={imgRef}
              src={thumb}
              alt={alt}
              loading={eager ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={index < 6 ? "high" : "auto"}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
              className={loaded ? "is-loaded" : undefined}
            />
          ) : (
            <div className="project-card__placeholder" aria-hidden>
              {project.title.slice(0, 1)}
            </div>
          )}
          {project.featured && <span className="badge project-card__featured">Featured</span>}
        </div>
        <div className="project-card__body">
          <div className="project-card__meta">
            {project.categories.slice(0, 2).map((c) => (
              <span key={c.id}>{c.name}</span>
            ))}
            {year && <span className="project-card__year">{year}</span>}
          </div>
          <h3>{project.title}</h3>
          {project.shortDescription && <p>{project.shortDescription}</p>}
        </div>
      </Link>
    </article>
  );
}
